import { isAxiosError, type AxiosError } from 'axios';

import { isRefusedLiveKitToken } from '@/common/utils/liveKitReconnectRefusal';

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const COMPACT_UUID_SEGMENT = /^[0-9a-f]{32}$/i;
const NUMERIC_SEGMENT = /^\d+$/;
const LONG_HEX_SEGMENT = /^[0-9a-f]{16,}$/i;
/** Cosmos-style opaque ids: long alphanumeric tokens with optional separators. */
const OPAQUE_ID_SEGMENT = /^[0-9a-zA-Z_-]{20,}$/;

function isAxiosLikeError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return true;
  }

  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const candidate = error as { isAxiosError?: boolean; name?: string };
  return candidate.isAxiosError === true || candidate.name === 'AxiosError';
}

function stripQueryString(url: string): string {
  const queryIndex = url.indexOf('?');
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}

function extractPathname(url: string): string {
  if (/^https?:\/\//i.test(url)) {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }

  return url;
}

function normalizeSlashes(path: string): string {
  const collapsed = path.replace(/\/+/g, '/');
  const withLeadingSlash = collapsed.startsWith('/') ? collapsed : `/${collapsed}`;
  if (withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/')) {
    return withLeadingSlash.slice(0, -1);
  }
  return withLeadingSlash || '/';
}

function isDynamicPathSegment(segment: string): boolean {
  return (
    UUID_SEGMENT.test(segment)
    || COMPACT_UUID_SEGMENT.test(segment)
    || NUMERIC_SEGMENT.test(segment)
    || LONG_HEX_SEGMENT.test(segment)
    || OPAQUE_ID_SEGMENT.test(segment)
  );
}

/**
 * Normalizes an Axios URL/path into a stable template for Sentry grouping.
 * Dynamic ids collapse to `:id`; static route words are preserved.
 */
export function templateUrlPath(url: string): string {
  const pathOnly = stripQueryString(extractPathname(url));
  const normalized = normalizeSlashes(pathOnly);

  if (normalized === '/') {
    return '/';
  }

  const segments = normalized
    .split('/')
    .filter(Boolean)
    .map((segment) => (isDynamicPathSegment(segment) ? ':id' : segment));

  return `/${segments.join('/')}`;
}

/**
 * Derives a custom Sentry fingerprint for Axios HTTP failures so operators can
 * triage by endpoint and status instead of one shared interceptor stack trace.
 */
export function axiosFingerprint(error: unknown): string[] | null {
  if (!isAxiosLikeError(error)) {
    return null;
  }

  const axiosError = error as AxiosError;
  const method = (axiosError.config?.method ?? 'UNKNOWN').toUpperCase();
  const status = String(axiosError.response?.status ?? 'network');
  const templatedUrl = templateUrlPath(axiosError.config?.url ?? 'unknown');

  return ['axios', method, status, templatedUrl];
}

/**
 * Groups LiveKit meeting reconnect failures in Sentry (refused token vs websocket).
 * Intentional client disconnects are not fingerprinted here.
 */
export function meetingReconnectFingerprint(error: unknown): string[] | null {
  if (!error || typeof error !== 'object') {
    return null;
  }
  const message = (error as Error).message ?? '';
  if (message === 'Client initiated disconnect') {
    return null;
  }
  if (isRefusedLiveKitToken(error)) {
    return ['meeting-reconnect-refused-token'];
  }
  if (message.toLowerCase().includes('websocket')) {
    return ['meeting-reconnect-websocket'];
  }
  return null;
}

const URBAN_VPN_M_ID_MESSAGE = "Cannot read properties of undefined (reading 'M_ID')";

const BROWSER_EXTENSION_PROTOCOLS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'edge-extension://',
] as const;

type SentryStackFrame = { filename?: string; abs_path?: string };

type SentryBeforeSendEvent = {
  exception?: {
    values?: Array<{
      value?: string;
      stacktrace?: { frames?: SentryStackFrame[] };
    }>;
  };
};

type SentryBeforeSendHint = { originalException?: unknown };

function stackFrameLocation(frame: SentryStackFrame): string {
  return frame.abs_path ?? frame.filename ?? '';
}

function isExtensionStackFrame(location: string): boolean {
  if (!location) {
    return false;
  }
  if (BROWSER_EXTENSION_PROTOCOLS.some((protocol) => location.startsWith(protocol))) {
    return true;
  }
  if (location === 'app:///200.js' || location.includes('app:///200.js')) {
    return true;
  }
  if (location.startsWith('app:///executors/') || location.includes('app:///executors/')) {
    return true;
  }
  return false;
}

/** React Native web rewrite paths under app:/// except extension bundles count as first-party. */
function isOurApplicationStackFrame(location: string): boolean {
  if (!location.startsWith('app:///')) {
    return false;
  }
  return !isExtensionStackFrame(location);
}

function collectStackFrameLocations(event: SentryBeforeSendEvent): string[] {
  const locations: string[] = [];
  for (const exception of event.exception?.values ?? []) {
    for (const frame of exception.stacktrace?.frames ?? []) {
      const location = stackFrameLocation(frame);
      if (location) {
        locations.push(location);
      }
    }
  }
  return locations;
}

function beforeSendErrorMessage(hint: SentryBeforeSendHint): string {
  const original = hint.originalException;
  if (original instanceof Error) {
    return original.message;
  }
  return String(original ?? '');
}

/**
 * Returns true when beforeSend should drop the event (Urban VPN M_ID noise or
 * extension-only stacks). First-party app:/// frames keep the event even for M_ID.
 */
export function shouldDropSentryNoiseEvent(
  event: SentryBeforeSendEvent,
  hint: SentryBeforeSendHint,
): boolean {
  const message = beforeSendErrorMessage(hint);
  const frameLocations = collectStackFrameLocations(event);
  const hasOurApplicationFrame = frameLocations.some(isOurApplicationStackFrame);

  if (message === URBAN_VPN_M_ID_MESSAGE && !hasOurApplicationFrame) {
    return true;
  }

  if (frameLocations.length > 0 && frameLocations.every(isExtensionStackFrame)) {
    return true;
  }

  return false;
}
